import { useEffect, useMemo, useState } from "react";

import { useBlocker, useParams } from "@tanstack/react-router";

import NavigationWarningModal from "@/components/NavigationWarningModal";
import { RecordingNavWarningModal } from "@/components/RecordingDock/RecordingNavWarningModal";
import { caseNumberFromPath } from "@/components/RecordingDock/dockVisibility";
import { useRecordingDock } from "@/components/RecordingDock/store/useRecordingDock";
import { ResizablePanels } from "@/components/ResizablePanels";
import { useCaseByCaseNumber } from "@/hooks/queries/use-cases";

import { useCaseDetail } from "@pages/ReportStudio/CaseDetail/context";

import { AudioTranscript } from "./components/AudioTranscript";
import { TabLinks } from "./components/TabLinks";
import { TranscribeAndUploadButtons } from "./components/TranscribeAndUploadButtons";
import type { RecordingSession } from "./hooks/useRecordingSession";

// A no-op session used before the RecordingHost has published the real one, so
// the UI can render without null-guards. Nothing can be recording yet.
const IDLE_SESSION: RecordingSession = {
  status: "idle",
  isActive: false,
  isStopped: false,
  isConnecting: false,
  isPaused: false,
  mediaRecorder: null,
  recordedAudioBlob: null,
  inputDeviceLabel: "Default microphone",
  roleOrder: [],
  speakerHints: [],
  start: async () => {},
  pause: () => {},
  resume: () => {},
  stop: () => {},
  cancel: () => {},
  reset: () => {},
};

const AudioTranscriberPage = () => {
  const { id: caseNumber } = useParams({ strict: false }) as { id: string };
  const { setRightContent } = useCaseDetail();

  // Examinee name for the case-boundary warning — makes clear which
  // recording will end. PHI: display-only, never logged.
  const { data: caseData } = useCaseByCaseNumber({
    case_number: caseNumber || "",
    enabled: !!caseNumber,
  });
  const examineeName = caseData?.examinee_name ?? null;

  const [selectedId, setSelectedId] = useState("1");
  const [isDirty, setIsDirty] = useState(false);

  // The live recording session is owned by the root RecordingHost so
  // it survives tab navigation; the page reads it from the store. Subscribing
  // to `status` keeps the live view re-rendering as the session progresses.
  const storeSession = useRecordingDock((s) => s.session);
  useRecordingDock((s) => s.status); // re-render on status transitions
  const owningCaseNumber = useRecordingDock((s) => s.caseNumber);
  const beginSession = useRecordingDock((s) => s.beginSession);
  const syncOwnerExaminee = useRecordingDock((s) => s.syncOwnerExaminee);

  // The dock's "Return to session" bumps returnRequestedAt — switch back to the
  // Transcript section (id "1") so the live view is revealed.
  const returnRequestedAt = useRecordingDock((s) => s.returnRequestedAt);
  useEffect(() => {
    if (returnRequestedAt > 0) setSelectedId("1");
  }, [returnRequestedAt]);

  // The examinee is snapshotted into the store at session start, but the case
  // query can resolve after Start Session is clicked — keep the owning case's
  // name fresh so the summarize-on-stop payload (host) always has it.
  useEffect(() => {
    if (caseNumber && examineeName) {
      syncOwnerExaminee(caseNumber, examineeName);
    }
  }, [caseNumber, examineeName, syncOwnerExaminee]);

  // Only surface the live store session's STATE on the case that OWNS it
  //. The session lives in a global store, so without this guard a
  // different case's scribe tab would read the same live/stopped state — e.g.
  // showing the "Recording complete" banner on a case that never recorded. A
  // non-owning case sees idle state, but keeps the real `start` so it can begin
  // its own recording (which then claims ownership via beginSession).
  const ownsSession = !!owningCaseNumber && owningCaseNumber === caseNumber;
  const liveSession = storeSession ?? IDLE_SESSION;
  const stateSession = ownsSession ? liveSession : IDLE_SESSION;

  // Wrap `start` so the store claims THIS case as the recording's owner (the
  // dock labels it, and the case-boundary guard keys off it). Start always
  // drives the real store session, even from an idle (non-owning) view.
  const session = useMemo<RecordingSession>(
    () => ({
      ...stateSession,
      start: async (maxSpeakers, roleOrder, speakerHints) => {
        if (caseNumber) {
          beginSession({ caseNumber, examineeName });
        }
        await liveSession.start(maxSpeakers, roleOrder, speakerHints, caseNumber);
      },
    }),
    [stateSession, liveSession, caseNumber, examineeName, beginSession]
  );

  // Block navigation (route or tab) while a live session is in progress; the
  // stopped/complete state no longer blocks (the transcript is being persisted).
  const isRecordingActive = session.isActive;

  // Set page header actions
  useEffect(() => {
    setRightContent(
      <TranscribeAndUploadButtons
        caseNumber={caseNumber}
        selectedId={selectedId}
        session={session}
      />
    );

    return () => {
      setRightContent(null);
    };
  }, [caseNumber, selectedId, session, setRightContent]);

  //: both unsaved section edits AND a live recording are case-scoped.
  // The doctor moves freely between the case's tabs (assessment prep, report
  // builder, case detail, …) with no interruption — a warning only fires when
  // navigation LEAVES the owning case (a different case, the case list, or any
  // non-case page). `leavesCase` centralises that boundary test for both
  // guards; in-case navigation is always frictionless.
  const leavesCase = (targetPath: string) =>
    caseNumberFromPath(targetPath) !== caseNumber;

  const blocker = useBlocker({
    shouldBlockFn: ({ next }) =>
      (isDirty || isRecordingActive) && leavesCase(next.pathname),
    // A hard close/reload during a live session or with unsaved edits still
    // warns via the browser's native prompt (UAT-04).
    enableBeforeUnload: () => isDirty || isRecordingActive,
    withResolver: true,
  });

  // A live recording leaving its case shows the two-button recording modal;
  // otherwise (unsaved edits only) the plain unsaved-edits modal.
  const blockedByRecordingLeave = blocker.status === "blocked" && isRecordingActive;

  const handleFeatureItemClick = (id: string) => {
    // Section switches happen within the scribe tab (no route change) and never
    // leave the case, so they're always frictionless now — neither
    // unsaved edits nor a live recording gate them.
    setSelectedId(id);
  };

  const handleConfirm = () => {
    // "Finish recording and leave": stop + save the session first (the host
    // dispatches the diarized summarize on stop), then navigate.
    if (isRecordingActive) {
      session.stop();
    }
    if (blocker.status === "blocked") {
      blocker.proceed();
    }
  };

  const handleCancel = () => {
    if (blocker.status === "blocked") {
      blocker.reset();
    }
  };

  // The unsaved-edits modal shows when leaving the case with dirty edits (and
  // no live recording); the two-button recording modal shows when a live
  // recording is leaving its case.
  const isDirtyConfirmOpen =
    blocker.status === "blocked" && !blockedByRecordingLeave;

  return (
    <>
      {/* Sections list (left) is drag-resizable against the content panel.
          Pixel width clamped 200–420px; resets to 288px (the prior fixed
          w-72) on reload — no persistence (MDRS-111). */}
      <ResizablePanels
        className="overflow-hidden"
        sizedSide="left"
        unit="px"
        defaultLeftWidth={288}
        minLeftWidth={200}
        maxLeftWidth={420}
        left={
          <TabLinks
            selectedId={selectedId}
            handleFeatureItemClick={handleFeatureItemClick}
          />
        }
        right={
          <AudioTranscript
            caseNumber={caseNumber}
            sectionId={selectedId}
            onDirtyChange={setIsDirty}
            session={session}
          />
        }
      />

      {/* Unsaved-edits guard — and section switches with a dirty
          field. Recording no longer routes through here. */}
      <NavigationWarningModal
        isOpen={isDirtyConfirmOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        isRecording={false}
      />

      {/* Case-boundary guard — a live recording is leaving its case.
          Two actions: stay (keep recording) or finish + save then leave. */}
      <RecordingNavWarningModal
        isOpen={!!blockedByRecordingLeave}
        examineeName={examineeName}
        onStay={handleCancel}
        onFinishAndLeave={handleConfirm}
      />
    </>
  );
};

export default AudioTranscriberPage;
